import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import PageTransition from "../components/PageTransition";
import ScrollReveal from "../components/ScrollReveal";
import Bubbles from "../components/Bubbles";
import JumpingFish from "../components/JumpingFish";
import ProductGrid from "../components/ProductGrid";
import ProductHighlightCarousel from "../components/ProductHighlightCarousel";
import ProductDemoVideo from "../components/ProductDemoVideo";
import ComparisonTable from "../components/ComparisonTable";
import JourneyVisualizer from "../components/JourneyVisualizer";
import Breadcrumbs from "../components/Breadcrumbs";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { PRODUCTS } from "../data/products";
import { TECH_STACK } from "../data/techStack";

export default function Products() {
  useDocumentTitle("Products & Services");
  const [expandedTech, setExpandedTech] = useState<string | null>(null);

  return (
    <PageTransition>
      <section className="relative overflow-hidden bg-gradient-to-b from-ocean-950 to-ocean-900 pt-32 pb-24 text-center text-foam-50 sm:pt-40 sm:pb-28">
        <Bubbles count={12} />
        <JumpingFish className="left-[10%] bottom-10 hidden sm:block" size={36} delay={0.5} />
        <div className="relative mx-auto max-w-2xl px-6">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xs font-semibold uppercase tracking-widest text-teal-300"
          >
            Products &amp; Services
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="mt-3 font-display text-4xl font-bold sm:text-5xl"
          >
            More Than Just an App
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-4 text-foam-100/70"
          >
            The full platform behind ZenFresh — for customers, drivers, suppliers, and our own
            operations team.
          </motion.p>
        </div>
      </section>

      <Breadcrumbs />

      {/* Product grid */}
      <section className="mx-auto max-w-6xl px-6 py-24">
        <ScrollReveal className="mx-auto max-w-xl text-center">
          <h2 className="font-display text-3xl font-bold text-ocean-950 sm:text-4xl">Our Products</h2>
        </ScrollReveal>
        <div className="mt-12">
          <ProductGrid />
        </div>
      </section>

      {/* Per-product detail sections */}
      {PRODUCTS.map((product, i) => (
        <section
          key={product.id}
          id={product.id}
          className={`scroll-mt-24 px-6 py-24 ${i % 2 === 1 ? "bg-foam-100" : ""}`}
        >
          <div className="mx-auto max-w-6xl">
            <ScrollReveal className="mx-auto max-w-2xl text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-400/15 text-teal-500">
                <product.icon className="h-6 w-6" strokeWidth={1.5} />
              </div>
              <h2 className="mt-4 font-display text-3xl font-bold text-ocean-950 sm:text-4xl">{product.name}</h2>
              {product.internalOnly && product.audienceNote && (
                <span className="mt-2 inline-block rounded-full bg-ocean-900/5 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-ocean-800/60">
                  {product.audienceNote}
                </span>
              )}
              <p className="mt-3 text-ocean-800/70">{product.summary}</p>
            </ScrollReveal>

            <ScrollReveal delay={0.08} className="mx-auto mt-10 max-w-2xl">
              <ProductDemoVideo demo={product.demoVideo} productName={product.name} />
            </ScrollReveal>

            <div className="mt-12">
              <ProductHighlightCarousel highlights={product.highlights} />
            </div>

            {product.ctaLabel && product.ctaHref && (
              <ScrollReveal delay={0.16} className="mt-10 text-center">
                <Link
                  to={product.ctaHref}
                  className="inline-flex items-center gap-2 rounded-full bg-teal-400 px-6 py-3 text-sm font-semibold text-ocean-950 transition-transform hover:scale-105"
                >
                  {product.ctaLabel}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </ScrollReveal>
            )}
          </div>
        </section>
      ))}

      {/* Why ZenFresh comparison */}
      <section id="why-zenfresh" className="scroll-mt-24 bg-ocean-900 px-6 py-24 text-foam-50">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal className="mx-auto max-w-xl text-center">
            <h2 className="font-display text-3xl font-bold sm:text-4xl">Why ZenFresh</h2>
            <p className="mt-4 text-foam-100/70">
              Compared to buying seafood the traditional way, at a fish market.
            </p>
          </ScrollReveal>
          <ScrollReveal delay={0.1} className="mt-12">
            <ComparisonTable />
          </ScrollReveal>
        </div>
      </section>

      {/* Technology & Engineering */}
      <section className="mx-auto max-w-4xl px-6 py-24">
        <ScrollReveal className="mx-auto max-w-xl text-center">
          <h2 className="font-display text-3xl font-bold text-ocean-950 sm:text-4xl">Built With</h2>
          <p className="mt-4 text-ocean-800/70">The technology behind the platform, in brief.</p>
        </ScrollReveal>

        <div className="mt-12 space-y-3">
          {TECH_STACK.map((tech, i) => {
            const expanded = expandedTech === tech.name;
            return (
              <ScrollReveal key={tech.name} delay={i * 0.05}>
                <div className="rounded-2xl bg-white shadow-sm ring-1 ring-ocean-900/5">
                  <button
                    type="button"
                    onClick={() => setExpandedTech((cur) => (cur === tech.name ? null : tech.name))}
                    aria-expanded={expanded}
                    className="flex w-full items-center gap-4 p-5 text-left"
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-teal-400/15 text-teal-500">
                      <tech.icon className="h-5 w-5" strokeWidth={1.5} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-display font-semibold text-ocean-950">{tech.name}</h3>
                      <p className="truncate text-sm text-ocean-800/60">{tech.reason}</p>
                    </div>
                    <ChevronDown
                      className={`h-4 w-4 shrink-0 text-ocean-800/40 transition-transform ${expanded ? "rotate-180" : ""}`}
                    />
                  </button>
                  {expanded && (
                    <p className="border-t border-ocean-900/5 px-5 pb-5 pt-3 text-sm leading-relaxed text-ocean-800/70">
                      {tech.usage}
                    </p>
                  )}
                </div>
              </ScrollReveal>
            );
          })}
        </div>
      </section>

      {/* Harbour to Home journey */}
      <section className="bg-foam-100 px-6 py-24">
        <div className="mx-auto max-w-6xl">
          <ScrollReveal className="mx-auto max-w-xl text-center">
            <h2 className="font-display text-3xl font-bold text-ocean-950 sm:text-4xl">Harbour to Home</h2>
            <p className="mt-4 text-ocean-800/70">
              Every order's journey, and how freshness is preserved at each stage.
            </p>
          </ScrollReveal>
          <div className="mt-12">
            <JourneyVisualizer />
          </div>
        </div>
      </section>
    </PageTransition>
  );
}
